/**
 * Whether a camera is still waiting for credentials — the signal that locks the
 * other Iris Connection tabs.
 *
 * There is no stored "authentication pending" state: a camera waits for
 * credentials while it is a transitory placeholder, or while state-sync reports
 * its video or control credentials as INVALID (a camera whose credential probe
 * failed is still committed with its selected connection). The sidebar
 * "Authenticate" badge and the tile chip both read that through
 * `deriveSetupAttention`; this hook passes the same inputs, so the tabs lock
 * exactly while that badge shows.
 *
 * Per rule: think-in-systems (ai.knowledge/global) — reuse the badge's verdict
 * instead of deriving a second one.
 */
import type { Device } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useDepot } from '#~/depot/hooks.js';
import { deriveSetupAttention } from '#~/device/setup-attention.js';
import { useTransitoryDevice } from '#~/device/transitory.js';
import { useMultiDeviceStateQuery } from '#~/statequery/hook.js';
import type { MultiDeviceSelector } from '#~/statequery/types.js';

// Copy only the per-aspect verdicts so unrelated status ticks keep the snapshot unchanged.
const authenticationStatusSelector = {
  minInterval: 200,
  transform: ({ status }) =>
    status
      ? {
          video: status.video && { authenticationStatus: status.video.authenticationStatus },
          movement: status.movement && {
            authenticationStatus: status.movement.authenticationStatus
          }
        }
      : undefined
} satisfies MultiDeviceSelector<unknown>;

/**
 * @param deviceId - The settings candidate.
 * @param roomId - Room whose records hold the candidate; `undefined` outside a room.
 * @returns `true` only while the camera is known to need credentials.
 */
export const useAuthenticationPending = (
  deviceId: Device['id'],
  roomId: T_Maybe<Room['id']>
): boolean => {
  const placeholder = useTransitoryDevice(deviceId);
  const status = useMultiDeviceStateQuery(authenticationStatusSelector)[deviceId];

  // Per rule: absent-data-gates-permissively (ai.knowledge/global) — only a
  // placeholder or a reported INVALID verdict locks. No status yet, UNKNOWN,
  // VALIDATING, ERROR, an offline bridge or a missing record leave tabs usable.
  return useDepot(
    (µ) =>
      deriveSetupAttention(
        (roomId ? µ.rooms[roomId]?.devices[deviceId] : undefined) ?? placeholder,
        undefined,
        status
      ) === 'authentication'
  );
};
